import { AxiosRequestConfig } from "axios";
import { httpService } from "./HttpServices";
import { ApiResponse } from "./UserService";

export interface FieldMapping {
  _id: string;
  organizationId: string;
  hubspotField: string;
  productField: string;
  direction: "bidirectional" | "hubspot_to_product" | "product_to_hubspot";
  objectType: "company" | "contact" | "deal" | "contract";
  status: boolean;
  syncOnCreate: boolean;
  syncOnUpdate: boolean;
  conflictResolution: "last_write_wins" | "hubspot_wins" | "product_wins";
  fieldType?: "string" | "number" | "date" | "boolean";
  transformation?: string;
  createdAt: string;
  updatedAt: string;
}

export interface HubSpotProperty {
  name: string;
  label: string;
  type: string;
  fieldType: string;
  options?: Array<{
    label: string;
    value: string;
  }>;
  description?: string;
}

export interface CreateFieldMappingRequest {
  hubspotField: string;
  productField: string;
  direction: "bidirectional" | "hubspot_to_product" | "product_to_hubspot";
  objectType: "company" | "contact" | "deal" | "contract";
  status?: boolean;
  syncOnCreate?: boolean;
  syncOnUpdate?: boolean;
  conflictResolution?: "last_write_wins" | "hubspot_wins" | "product_wins";
  fieldType?: "string" | "number" | "date" | "boolean";
  transformation?: string;
}

export interface UpdateFieldMappingRequest
  extends Partial<CreateFieldMappingRequest> {}

export interface FieldMappingsResponse {
  mappings: FieldMapping[];
}

export interface HubSpotPropertiesResponse {
  properties: HubSpotProperty[];
}

export interface DeleteFieldMappingResponse {
  id: string;
}

export interface SyncFieldMappingResponse {
  mappingId: string;
  message: string;
}

export class FieldMappingService {
  async getFieldMappings(
    filters?: {
      objectType?: "company" | "contact" | "deal" | "contract";
      status?: boolean;
    },
    config?: AxiosRequestConfig
  ): Promise<{ data: FieldMappingsResponse; message: string }> {
    try {
      const params: Record<string, string> = {};
      if (filters?.objectType) {
        params.objectType = filters.objectType;
      }
      if (filters?.status !== undefined) {
        params.status = String(filters.status);
      }

      const response = await httpService.get<ApiResponse<FieldMappingsResponse>>(
        "/hubspot/field-mappings",
        {
          ...config,
          params,
        }
      );

      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        "Failed to fetch field mappings.";
      throw new Error(errorMessage);
    }
  }

  async getHubSpotProperties(
    objectType: "company" | "contact" | "deal" | "contract",
    config?: AxiosRequestConfig
  ): Promise<{ data: HubSpotPropertiesResponse; message: string }> {
    try {
      const response = await httpService.get<
        ApiResponse<HubSpotPropertiesResponse>
      >("/hubspot/field-mappings/properties", {
        ...config,
        params: { objectType },
      });

      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        "Failed to fetch HubSpot properties.";
      throw new Error(errorMessage);
    }
  }

  async createFieldMapping(
    data: CreateFieldMappingRequest,
    config?: AxiosRequestConfig
  ): Promise<{ data: { mapping: FieldMapping }; message: string }> {
    try {
      const response = await httpService.post<
        ApiResponse<{ mapping: FieldMapping }>
      >("/hubspot/field-mappings", data, config);

      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        "Failed to create field mapping.";
      throw new Error(errorMessage);
    }
  }

  async updateFieldMapping(
    id: string,
    data: UpdateFieldMappingRequest,
    config?: AxiosRequestConfig
  ): Promise<{ data: { mapping: FieldMapping }; message: string }> {
    try {
      const response = await httpService.put<
        ApiResponse<{ mapping: FieldMapping }>
      >(`/hubspot/field-mappings/${id}`, data, config);

      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        "Failed to update field mapping.";
      throw new Error(errorMessage);
    }
  }

  async deleteFieldMapping(
    id: string,
    config?: AxiosRequestConfig
  ): Promise<{ data: DeleteFieldMappingResponse; message: string }> {
    try {
      const response = await httpService.delete<
        ApiResponse<DeleteFieldMappingResponse>
      >(`/hubspot/field-mappings/${id}`, config);

      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        "Failed to delete field mapping.";
      throw new Error(errorMessage);
    }
  }

  async syncFieldMapping(
    id: string,
    config?: AxiosRequestConfig
  ): Promise<{ data: SyncFieldMappingResponse; message: string }> {
    try {
      const response = await httpService.post<
        ApiResponse<SyncFieldMappingResponse>
      >(`/hubspot/field-mappings/${id}/sync`, {}, config);

      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        "Failed to initiate field mapping sync.";
      throw new Error(errorMessage);
    }
  }
}

export const fieldMappingService = new FieldMappingService();

